import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { FileStillUploadingError, FileUploadFailedError, stagedUploadId } from '@ee/common/custom-field';
import { CustomFieldType } from '@ee/common/enums';
import { omitUnaskedEvictionReason } from '@ee/common/forms';
import { Eviction, IntakeUpload, Tenant } from '@ee/common/models';
import { normalizeEvictionReasons } from '@ee/common/utils';
import { FieldDefinitionView, LayoutPageView } from './intake-layout.model';
import { placementsOf } from './intake-form-controls';

/** The reason question, in either display mode (EE-392). */
const REASON_FIELD_KEYS = new Set(['eviction_reason', 'eviction_reasons']);

/**
 * The v2 form's answers, in the shape the submission endpoint expects.
 *
 * The wizard holds one flat group keyed by storage key, because that is what the layout describes. The
 * endpoint wants `eviction_details` with the built-in fields at the top level and everything a firm
 * invented under `custom_fields`, plus the account and business details as their own objects — the same
 * shape `IntakeEvictionFormService.generateEvictionDetails` produces by merging its five step groups.
 *
 * Pure, and tested, because this is the filing. A mapper that quietly drops a field produces a case
 * that is missing something nobody notices until a court does.
 */

/** Page one's questions, which create the client's account rather than describing the case. */
const USER_KEYS = ['first_name', 'last_name', 'email', 'phone_number'] as const;
const CLIENT_KEYS = ['company_name', 'street_address_1', 'street_address_2', 'city', 'state', 'zip_code'] as const;

export interface SubmissionParts {
  evictionDetails: Eviction;
  /**
   * Loose records rather than `User` / `Organization`.
   *
   * Only the handful of keys page one asks for are ever set, and typing them as the full entities would
   * claim an id, a role and a subscription this has no business inventing.
   */
  userDetails?: Record<string, unknown>;
  clientDetails?: Record<string, unknown>;
  /** One per answered file placement, typed by its key: the staged uploads the endpoint attaches. */
  uploads: IntakeUpload[];
}

function scalar(value: unknown): unknown {
  return value === '' ? null : value;
}

/** The placement key. Checkboxes and a radio both write here; the case stores the list beside it. */
const EVICTION_REASON_KEY = 'eviction_reason';

/**
 * The pair EE-392 stores: `eviction_reasons` is the list, `eviction_reason` is its first entry.
 *
 * A radio writes one string and checkboxes write the list. Both become the same pair, so a checkbox
 * firm is not saved as only the first box and a radio firm is not saved as a one-element mismatch.
 * Blank, whitespace and duplicates are dropped the way the server normalizer drops them.
 */
export function evictionReasonAnswers(value: unknown): { eviction_reasons: string[]; eviction_reason: string | null } {
  const reasons = normalizeEvictionReasons(Array.isArray(value) ? value : [value]);
  return { eviction_reasons: reasons, eviction_reason: reasons[0] ?? null };
}

/**
 * Form values as the layout-rule evaluator should read them.
 *
 * The control holds the whole answer. The case stores `eviction_reason` as the first entry and
 * `eviction_reasons` as the list, which is what the server's layout rules read. Passing the raw
 * checkbox array would compare the joined text here and the first reason on the server.
 *
 * Document tokens and the EE-392 condition rules do not read this view. They read the submitted pair,
 * and there `eviction_reason ==` means the list includes the value and `in` means the lists overlap.
 */
export function valuesForEvictionReasonRules(values: Record<string, unknown>): Record<string, unknown> {
  if (!Object.prototype.hasOwnProperty.call(values, EVICTION_REASON_KEY)) {
    return values;
  }
  return { ...values, ...evictionReasonAnswers(values[EVICTION_REASON_KEY]) };
}

/**
 * Which keys are the product's and which the firm's.
 *
 * From `FieldDefinitionView.origin`, which is the only place that knows. Guessing by prefix is how a
 * firm's field called `property_note` ends up at the top level of `Eviction`, where nothing reads it.
 */
function isCustom(key: string, definitions: Map<string, FieldDefinitionView>): boolean {
  return definitions.get(key)?.origin === 'CUSTOM';
}

/**
 * The tenants, one object per panel the client filled in.
 *
 * Empty rows are dropped: "Add another tenant" then changing your mind leaves a group with nothing in
 * it, and submitting it names a tenant with no name on the complaint.
 */
function tenantsOf(form: FormGroup): Tenant[] {
  const array = form.get('tenants') as FormArray | null;
  return ((array?.controls ?? []) as FormGroup[])
    .map(group => group.getRawValue() as Record<string, unknown>)
    .filter(tenant => Object.values(tenant).some(value => value !== null && value !== undefined && value !== ''))
    .map(tenant => tenant as unknown as Tenant);
}

/**
 * A file answer as the staged upload the server attaches.
 *
 * The control holds the `File` (`ee-uploader` writes an array of one) and the submit is JSON, where a
 * `File` is `{}`. The upload id sits beside the control (`staged-upload.ts`); a file without one is still
 * uploading, or failed, and is refused rather than sent without it — the server would drop it silently.
 */
function uploadOf(key: string, control: AbstractControl | null, definition: FieldDefinitionView | undefined): IntakeUpload | null {
  const value = control?.getRawValue();
  const file = Array.isArray(value) ? value[0] : value;
  if (!control || !(file instanceof File)) {
    return null;
  }
  const label = definition?.label || key;
  if (control.hasError('uploadFailed')) {
    throw new FileUploadFailedError(label);
  }
  const uploadId = stagedUploadId(control);
  if (!uploadId) {
    throw new FileStillUploadingError(label, 'submit');
  }
  return { type: key, upload_id: uploadId };
}

export function toSubmissionParts(
  pages: LayoutPageView[],
  form: FormGroup,
  definitions: Map<string, FieldDefinitionView>,
  options: { includeAccount?: boolean; hidden?: ReadonlySet<string> } = {}
): SubmissionParts {
  const includeAccount = options.includeAccount !== false;
  const values = form.getRawValue() as Record<string, unknown>;

  const builtIn: Record<string, unknown> = {};
  const custom: Record<string, unknown> = {};
  const uploads: IntakeUpload[] = [];

  // Only what the form actually placed. Walking the raw values instead would submit a control left
  // behind by a field the firm has since removed.
  placementsOf(pages).forEach(item => {
    const keys =
      item.kind === 'COMPOSITE' && item.composite_type
        ? Object.keys(values).filter(key => key.startsWith('property_'))
        : [item.field_key ?? ''];

    keys
      .filter(key => key && key in values)
      .forEach(key => {
        if (USER_KEYS.includes(key as (typeof USER_KEYS)[number])) {
          return;
        }
        if (CLIENT_KEYS.includes(key as (typeof CLIENT_KEYS)[number])) {
          return;
        }
        if (definitions.get(key)?.type === CustomFieldType.FILE_UPLOAD) {
          const upload = uploadOf(key, form.get(key), definitions.get(key));
          if (upload && !uploads.some(u => u.upload_id === upload.upload_id)) {
            uploads.push(upload);
          }
          return;
        }
        // Either placement files the pair, so a list never goes out without its first-reason mirror.
        if (REASON_FIELD_KEYS.has(key)) {
          Object.assign(builtIn, evictionReasonAnswers(values[key]));
          return;
        }
        (isCustom(key, definitions) ? custom : builtIn)[key] = scalar(values[key]);
      });
  });

  /*
   * `plaintiff` is the intake key and `company_name` is what `Eviction` carries for the same thing —
   * the two disagree in the codebase and the layout uses `plaintiff` because that is what a document
   * template can address. Translated here rather than anywhere else, so only one place knows.
   */
  if ('plaintiff' in builtIn) {
    builtIn['company_name'] = builtIn['plaintiff'];
    delete builtIn['plaintiff'];
  }

  // A reason the form does not ask — not placed, or hidden by a rule — must not keep a default. An
  // absent key is not the same as "nothing selected": the server would apply the form default and the
  // court documents would check Unpaid Rent (ISS-163).
  let evictionDetails = {
    ...builtIn,
    tenants: tenantsOf(form),
    custom_fields: custom
  } as unknown as Eviction;
  if (!evictionReasonAsked(pages, options.hidden)) {
    evictionDetails = omitUnaskedEvictionReason(evictionDetails);
  }

  if (!includeAccount) {
    return { evictionDetails, uploads };
  }

  const pick = (keys: readonly string[]): Record<string, unknown> =>
    keys.reduce<Record<string, unknown>>((acc, key) => {
      if (key in values) {
        acc[key] = scalar(values[key]);
      }
      return acc;
    }, {});

  const userDetails = pick(USER_KEYS);
  const clientDetails = pick(CLIENT_KEYS);

  return {
    evictionDetails,
    uploads,
    // Omitted rather than sent empty: the server synthesizes them for a signed-in client, and an object
    // of nulls is not the same as "not supplied".
    userDetails: Object.keys(userDetails).length ? userDetails : undefined,
    clientDetails: Object.keys(clientDetails).length ? clientDetails : undefined
  };
}

/**
 * True when a reason placement is on screen.
 *
 * A hidden section hides every field in it even when the field key itself is not in `hidden` (a HIDE
 * rule can target the section). No placement at all is the v2 reading of `show: false`: the field was
 * never asked, so it is not a reason to file.
 */
function evictionReasonAsked(pages: LayoutPageView[], hidden: ReadonlySet<string> | undefined): boolean {
  return pages.some(page =>
    (page.sections ?? []).some(section => {
      if (hidden?.has(section.key)) {
        return false;
      }
      return (section.rows ?? []).some(row =>
        (row.items ?? []).some(item => {
          const key = item.field_key ?? '';
          return REASON_FIELD_KEYS.has(key) && !hidden?.has(key);
        })
      );
    })
  );
}
